"use client";

import React, {useEffect, useMemo, useState} from "react";
import Cookies from "js-cookie";
import {Check, X} from "lucide-react";

export interface Attributi {
    aacodart: string;
    aacodatt: string;
    descr1?: string | null;
    campo1?: string | null;
    descr2?: string | null;
    campo2?: string | null;
    descr3?: string | null;
    campo3?: string | null;
}

interface CarAttributiProps {
    codiceArticolo: string;
    authToken?: string | null;
}

type FetchStatus = "idle" | "loading" | "success" | "empty" | "error";

const formatValue = (value: string | null | undefined): React.ReactNode => {
    if (!value || value.trim() === "") {
        return "-";
    }

    if (value === "N") {
        return <X className="h-5 w-5 text-red-500" aria-label="No" />;
    }

    if (value === "S") {
        return <Check className="h-5 w-5 text-green-500" aria-label="Yes" />;
    }

    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (dateRegex.test(value)) {
        const [day, month, year] = value.split("-");
        return `${day}/${month}/${year}`;
    }

    if (!Number.isNaN(Number(value))) {
        return value;
    }

    return value;
};

const AttributeItem = ({label, value}: { label: string; value: string | null | undefined }) => (
    <div className="flex items-center justify-between py-2 border-b border-muted last:border-b-0">
        <span className="font-medium text-sm md:text-base mr-4">{label.toUpperCase()}</span>
        <span className="text-sm md:text-base text-right flex items-center gap-2">{formatValue(value)}</span>
    </div>
);

const parseResponse = (payload: unknown): Attributi[] => {
    if (!payload) {
        return [];
    }

    if (Array.isArray(payload)) {
        return payload as Attributi[];
    }

    if (typeof payload === "object" && "data" in payload) {
        const data = (payload as { data?: unknown }).data;
        if (Array.isArray(data)) {
            return data as Attributi[];
        }
    }

    return [];
};

const CarAttributi: React.FC<CarAttributiProps> = ({codiceArticolo, authToken}) => {
    const [attributi, setAttributi] = useState<Attributi[]>([]);
    const [status, setStatus] = useState<FetchStatus>("idle");

    useEffect(() => {
        if (!codiceArticolo) {
            return;
        }

        const effectiveAuthToken = authToken ?? Cookies.get("authToken");
        if (!effectiveAuthToken) {
            setStatus("empty");
            return;
        }

        const controller = new AbortController();

        const fetchAttributi = async () => {
            try {
                setStatus("loading");
                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/dashboard/attributi/${codiceArticolo}`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${effectiveAuthToken}`,
                        "Content-Type": "application/json",
                    },
                    signal: controller.signal,
                    cache: "no-store",
                });

                if (!response.ok) {
                    throw new Error(`Unable to load attributes (${response.status})`);
                }

                const data = await response.json();
                const parsed = parseResponse(data);

                if (parsed.length === 0) {
                    setStatus("empty");
                    setAttributi([]);
                    return;
                }

                setAttributi(parsed);
                setStatus("success");
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }
                console.error("Errore durante il recupero degli attributi del veicolo:", error);
                setStatus("error");
            }
        };

        fetchAttributi();

        return () => {
            controller.abort();
        };
    }, [authToken, codiceArticolo]);

    const attributesToDisplay = useMemo(() => {
        return attributi
            .map(attributo => {
                const fields = [
                    {descr: attributo.descr1, campo: attributo.campo1},
                    {descr: attributo.descr2, campo: attributo.campo2},
                    {descr: attributo.descr3, campo: attributo.campo3},
                ].filter(({descr, campo}) => Boolean(descr) && campo !== null && campo !== undefined && `${campo}`.trim() !== "");

                return {
                    ...attributo,
                    fields,
                };
            })
            .filter(item => item.fields.length > 0);
    }, [attributi]);

    if (status === "loading") {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-muted p-6">
                <h3 className="text-lg font-semibold mb-2">Attributi</h3>
                <p className="text-sm text-muted-foreground">Caricamento attributi in corso…</p>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-muted p-6">
                <h3 className="text-lg font-semibold mb-2">Attributi</h3>
                <p className="text-sm text-red-500">Impossibile caricare gli attributi del veicolo.</p>
            </div>
        );
    }

    if (status === "empty" || attributesToDisplay.length === 0) {
        return null;
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-muted p-6">
            <h3 className="text-lg font-semibold mb-4">Attributi</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {attributesToDisplay.map((attributo, index) => (
                    <div key={`${attributo.aacodatt}-${index}`} className="space-y-2">
                        {attributo.fields.map((field, idx) => (
                            <AttributeItem key={`${attributo.aacodatt}-${idx}`} label={field.descr as string} value={field.campo as string} />
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CarAttributi;
